import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';

// API functions using axios and the V2 API endpoints
const API_BASE_URL = '/api/v2';

const searchCandidatesAPI = async (searchParams) => {
  console.log('Searching candidates with params:', searchParams);
  try {
    // Get token from localStorage as fallback
    const token = localStorage.getItem('hv_jwt');
    
    // Convert our search parameters to the format expected by the API
    const queryParams = {
      q: searchParams.query || '',
      page: searchParams.page || 1,
      limit: searchParams.limit || 20,
      ...searchParams.filters
    };
    
    const config = {
      params: queryParams
    };
    
    // Add auth header if token exists and axios interceptor might not be working
    if (token) {
      config.headers = {
        'Authorization': `Bearer ${token}`
      };
    }
    
    const response = await axios.get(`${API_BASE_URL}/candidates/search`, config);
    return response.data.data || [];
  } catch (error) {
    console.error('Error searching candidates:', error);
    if (error.response?.status === 401) {
      throw new Error('Authentication failed. Please login again.');
    }
    throw error;
  }
};

const mergeCandidatesAPI = async (candidateIds) => {
  console.log('Merging candidates with IDs:', candidateIds);
  try {
    const token = localStorage.getItem('hv_jwt');
    
    // Select the first candidate as the primary candidate
    const primaryId = candidateIds[0];
    const otherIds = candidateIds.slice(1);
    
    const config = {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    };
    
    const response = await axios.post(`${API_BASE_URL}/candidates/${primaryId}/merge`, {
      candidateIds: otherIds
    }, config);
    
    return response.data;
  } catch (error) {
    console.error('Error merging candidates:', error);
    throw error;
  }
};

const bulkEditCandidatesAPI = async (candidateIds, edits) => {
  console.log('Bulk editing candidates with IDs:', candidateIds, 'Edits:', edits);
  try {
    const token = localStorage.getItem('hv_jwt');
    
    const config = {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    };
    
    const response = await axios.patch(`${API_BASE_URL}/candidates/bulk-edit`, {
      candidateIds,
      edits
    }, config);
    
    return response.data;
  } catch (error) {
    console.error('Error bulk editing candidates:', error);
    throw error;
  }
};

const CandidateDashboard = () => {
  // Auth state
  const { user, auth } = useAuth();
  
  // Core state
  const [candidates, setCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mergeMessage, setMergeMessage] = useState('');
  const [bulkEditStatus, setBulkEditStatus] = useState('');
  
  // Sidebar state
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Advanced search state
  const [filters, setFilters] = useState({
    skills: '',
    experience_min: '',
    experience_max: '',
    location: '',
    organization: '',
    tags: '',
    status: '',
    // Resume-based filters
    hasResume: '',
    resumeText: '',
    resumeUrl: '',
    // Additional field filters
    hasLinkedin: '',
    compensation_min: '',
    compensation_max: '',
    currentCompany: '',
    currentRole: '',
    headline: ''
  });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 20
  });

  // Table column management
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    email: true,
    phone: true,
    skills: true,
    experience_yrs: true,
    currentLocation: true,
    currentCompany: true,
    currentRole: true,
    compensation: true,
    Status: true,
    linkedin: true,
    resume: true,
    headline: true
  });

  const allColumns = [
    { key: 'name', label: 'Name', width: '200px' },
    { key: 'email', label: 'Email', width: '200px' },
    { key: 'phone', label: 'Phone', width: '150px' },
    { key: 'skills', label: 'Skills', width: '250px' },
    { key: 'experience_yrs', label: 'Experience', width: '100px' },
    { key: 'currentLocation', label: 'Location', width: '150px' },
    { key: 'currentCompany', label: 'Company', width: '180px' },
    { key: 'currentRole', label: 'Role', width: '150px' },
    { key: 'compensation', label: 'Compensation', width: '130px' },
    { key: 'Status', label: 'Status', width: '100px' },
    { key: 'linkedin', label: 'LinkedIn', width: '100px' },
    { key: 'resume', label: 'Resume', width: '100px' },
    { key: 'headline', label: 'Headline', width: '200px' }
  ];

  // Initial load or search
  useEffect(() => {
    // Check if user is authenticated
    if (!auth?.token && !localStorage.getItem('hv_jwt')) {
      setError('Please login to access the candidate dashboard.');
      return;
    }
    
    // Load initial data on component mount
    handleSearch();
  }, [pagination.currentPage, auth?.token]);

  const handleSearch = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Construct search parameters object with filters
      const searchParams = { 
        query: searchTerm,
        page: pagination.currentPage,
        limit: pagination.limit,
        filters: { 
          ...Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== '')
          )
        }
      };
      
      const response = await axios.get(`${API_BASE_URL}/candidates/search`, { 
        params: { 
          q: searchTerm,
          page: pagination.currentPage,
          limit: pagination.limit,
          ...searchParams.filters
        } 
      });
      
      const { data, pagination: paginationData } = response.data;
      
      setCandidates(data);
      setPagination({
        currentPage: paginationData.current_page,
        totalPages: paginationData.total_pages,
        totalRecords: paginationData.total,
        limit: paginationData.per_page
      });
    } catch (err) {
      setError('Failed to fetch candidates: ' + (err.response?.data?.message || err.message));
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const clearAllFilters = () => {
    setFilters({
      skills: '',
      experience_min: '',
      experience_max: '',
      location: '',
      organization: '',
      tags: '',
      status: '',
      hasResume: '',
      resumeText: '',
      resumeUrl: '',
      hasLinkedin: '',
      compensation_min: '',
      compensation_max: '',
      currentCompany: '',
      currentRole: '',
      headline: ''
    });
    setSearchTerm('');
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prevSelected =>
      prevSelected.includes(candidateId)
        ? prevSelected.filter(id => id !== candidateId)
        : [...prevSelected, candidateId]
    );
  };
  
  const handleViewCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setSidebarOpen(true);
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleAdvancedSearch = () => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1  // Reset to first page when performing a new search
    }));
    handleSearch();
  };
  
  const handleColumnToggle = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Quick filter functions
  const handleQuickFilter = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    // Auto-apply the filter
    setTimeout(handleSearch, 100);
  };

  // Computed values for quick stats
  const candidatesWithResume = useMemo(() => 
    candidates.filter(c => c.resume && c.resume.length > 0), [candidates]
  );
  
  const candidatesWithLinkedin = useMemo(() => 
    candidates.filter(c => c.linkedin && c.linkedin.length > 0), [candidates]
  );

  const statusCounts = useMemo(() => {
    const counts = {};
    candidates.forEach(c => {
      const status = c.Status || 'Unknown';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [candidates]);

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  const handleMergeSelected = async () => {
    if (selectedCandidates.length < 2) {
      setMergeMessage('Please select at least two candidates to merge.');
      return;
    }
    setIsLoading(true);
    setMergeMessage('');
    try {
      const response = await mergeCandidatesAPI(selectedCandidates);
      if (response.success) {
        setMergeMessage(response.message);
        setSelectedCandidates([]);
        handleSearch(); // Refresh list
      } else {
        setMergeMessage(response.message || 'Failed to merge candidates.');
      }
    } catch (err) {
      setMergeMessage('An error occurred while merging candidates: ' + err.message);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkEdit = async (edits) => {
    if (selectedCandidates.length === 0) {
      setBulkEditStatus('Please select candidates to edit.');
      return;
    }
    setIsLoading(true);
    setBulkEditStatus('');
    try {
      const response = await bulkEditCandidatesAPI(selectedCandidates, edits);
      if (response.success) {
        setBulkEditStatus(response.message || 'Candidates updated successfully.');
        setSelectedCandidates([]);
        handleSearch(); // Refresh list
      } else {
        setBulkEditStatus(response.message || 'Failed to bulk edit candidates.');
      }
    } catch (err) {
      setBulkEditStatus('An error occurred during bulk edit: ' + err.message);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const detectDuplicates = () => {
    // Simple duplicate detection based on email and name
    const emailMap = {};
    const nameMap = {};
    const potentialDuplicates = [];
    
    candidates.forEach(candidate => {
      if (emailMap[candidate.email?.toLowerCase()]) {
        potentialDuplicates.push({
          id: candidate._id,
          duplicateId: emailMap[candidate.email?.toLowerCase()].id,
          reason: 'Same email address'
        });
      } else if (candidate.email) {
        emailMap[candidate.email.toLowerCase()] = {
          id: candidate._id,
          name: candidate.name
        };
      }
      
      // Check for similar names as well
      if (candidate.name && nameMap[candidate.name.toLowerCase()]) {
        // Only add if not already detected by email
        if (!potentialDuplicates.some(d => d.id === candidate._id || d.duplicateId === candidate._id)) {
          potentialDuplicates.push({
            id: candidate._id,
            duplicateId: nameMap[candidate.name.toLowerCase()].id,
            reason: 'Similar name'
          });
        }
      } else if (candidate.name) {
        nameMap[candidate.name.toLowerCase()] = {
          id: candidate._id,
          email: candidate.email
        };
      }
    });
    
    return potentialDuplicates;
  };

  // Cell renderer for different data types
  const renderCell = (candidate, columnKey) => {
    const value = candidate[columnKey];
    
    switch (columnKey) {
      case 'name':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              checked={selectedCandidates.includes(candidate._id)}
              onChange={() => handleSelectCandidate(candidate._id)}
            />
            <span style={{ fontWeight: 'bold' }}>{value || 'N/A'}</span>
          </div>
        );
      
      case 'email':
        return value ? (
          <a href={`mailto:${value}`} style={{ color: '#4a90e2', textDecoration: 'none' }}>
            {value}
          </a>
        ) : 'N/A';
      
      case 'phone':
        return value ? (
          <a href={`tel:${value}`} style={{ color: '#4a90e2', textDecoration: 'none' }}>
            {value}
          </a>
        ) : 'N/A';
      
      case 'skills':
        return value ? (
          <div style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {Array.isArray(value) ? value.join(', ') : value}
          </div>
        ) : 'N/A';
      
      case 'experience_yrs':
        return value ? `${value} years` : 'N/A';
      
      case 'compensation':
        return value ? (
          typeof value === 'number' ? `$${value.toLocaleString()}` : value
        ) : 'N/A';
      
      case 'Status':
        return (
          <span style={{
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 'bold',
            backgroundColor: getStatusColor(value),
            color: 'white'
          }}>
            {value || 'Unknown'}
          </span>
        );
      
      case 'linkedin':
        return value ? (
          <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>
            🔗 LinkedIn
          </a>
        ) : '❌';
      
      case 'resume':
        return value && value.length > 0 ? (
          <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>
            📄 Resume
          </a>
        ) : '❌';
      
      case 'headline':
        return (
          <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value || 'N/A'}
          </div>
        );
      
      default:
        return value || 'N/A';
    }
  };

  // Helper function for status colors
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return '#4caf50';
      case 'interviewing': return '#2196f3';
      case 'hired': return '#8bc34a';
      case 'rejected': return '#f44336';
      case 'on hold': return '#ff9800';
      case 'inactive': return '#9e9e9e';
      default: return '#607d8b';
    }
  };

  // Detect potential duplicate candidates
  const potentialDuplicates = detectDuplicates();

  return (
    <div style={{ padding: '20px', maxWidth: '100vw', overflow: 'hidden' }}>
      <h1>Candidate Management Dashboard</h1>
      
      {/* Authentication Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#f0f0f0', 
          marginBottom: '20px',
          fontSize: '12px',
          borderRadius: '4px'
        }}>
          <strong>Auth Debug:</strong><br/>
          User: {user?.email || 'Not logged in'}<br/>
          Role: {user?.role || 'No role'}<br/>
          Token exists: {auth?.token ? 'Yes' : 'No'}<br/>
          LocalStorage token: {localStorage.getItem('hv_jwt') ? 'Yes' : 'No'}
        </div>
      )}

      {/* Search Section with Advanced Search Toggle */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', marginBottom: '10px' }}>
          <input
            type="text"
            placeholder="Search candidates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flexGrow: 1, marginRight: '10px', padding: '8px' }}
          />
          <button onClick={handleSearch} disabled={isLoading} style={{ padding: '8px 12px', marginRight: '10px' }}>
            {isLoading ? 'Searching...' : 'Search'}
          </button>
          <button 
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)} 
            style={{ padding: '8px 12px' }}
          >
            {showAdvancedSearch ? 'Hide Advanced' : 'Advanced Search'}
          </button>
        </div>
        
        {/* Advanced Search Filters */}
        {showAdvancedSearch && (
          <div style={{ 
            padding: '20px', 
            border: '1px solid #ddd', 
            borderRadius: '8px',
            marginBottom: '15px',
            backgroundColor: '#f9f9f9'
          }}>
            <h3 style={{ marginTop: '0', marginBottom: '15px' }}>Advanced Filters</h3>
            
            {/* Quick Filter Buttons */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '10px' }}>Quick Filters</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <button 
                  onClick={() => handleQuickFilter('hasResume', 'true')}
                  style={{ 
                    padding: '6px 12px', 
                    backgroundColor: filters.hasResume === 'true' ? '#4a90e2' : '#f1f1f1',
                    color: filters.hasResume === 'true' ? 'white' : 'black',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  With Resume ({candidatesWithResume.length})
                </button>
                <button 
                  onClick={() => handleQuickFilter('hasResume', 'false')}
                  style={{ 
                    padding: '6px 12px', 
                    backgroundColor: filters.hasResume === 'false' ? '#4a90e2' : '#f1f1f1',
                    color: filters.hasResume === 'false' ? 'white' : 'black',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Without Resume ({candidates.length - candidatesWithResume.length})
                </button>
                <button 
                  onClick={() => handleQuickFilter('hasLinkedin', 'true')}
                  style={{ 
                    padding: '6px 12px', 
                    backgroundColor: filters.hasLinkedin === 'true' ? '#4a90e2' : '#f1f1f1',
                    color: filters.hasLinkedin === 'true' ? 'white' : 'black',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  With LinkedIn ({candidatesWithLinkedin.length})
                </button>
                {Object.entries(statusCounts).map(([status, count]) => (
                  <button 
                    key={status}
                    onClick={() => handleQuickFilter('status', status)}
                    style={{ 
                      padding: '6px 12px', 
                      backgroundColor: filters.status === status ? '#4a90e2' : '#f1f1f1',
                      color: filters.status === status ? 'white' : 'black',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {status} ({count})
                  </button>
                ))}
              </div>
            </div>

            {/* Basic Filters */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ marginBottom: '10px' }}>Basic Information</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Skills</label>
                  <input
                    type="text"
                    name="skills"
                    placeholder="e.g., React, Node.js"
                    value={filters.skills}
                    onChange={handleFilterChange}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Location</label>
                  <input
                    type="text"
                    name="location"
                    placeholder="e.g., New York, Remote"
                    value={filters.location}
                    onChange={handleFilterChange}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Current Company</label>
                  <input
                    type="text"
                    name="currentCompany"
                    placeholder="e.g., Google, Amazon"
                    value={filters.currentCompany}
                    onChange={handleFilterChange}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Current Role</label>
                  <input
                    type="text"
                    name="currentRole"
                    placeholder="e.g., Senior Developer"
                    value={filters.currentRole}
                    onChange={handleFilterChange}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
              </div>
            </div>

            {/* Experience and Compensation */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ marginBottom: '10px' }}>Experience & Compensation</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Experience Range (Years)</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="number"
                      name="experience_min"
                      placeholder="Min"
                      min="0"
                      value={filters.experience_min}
                      onChange={handleFilterChange}
                      style={{ width: '50%', padding: '8px' }}
                    />
                    <input
                      type="number"
                      name="experience_max"
                      placeholder="Max"
                      min="0"
                      value={filters.experience_max}
                      onChange={handleFilterChange}
                      style={{ width: '50%', padding: '8px' }}
                    />
                  </div>
                </div>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Compensation Range</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      type="number"
                      name="compensation_min"
                      placeholder="Min"
                      min="0"
                      value={filters.compensation_min}
                      onChange={handleFilterChange}
                      style={{ width: '50%', padding: '8px' }}
                    />
                    <input
                      type="number"
                      name="compensation_max"
                      placeholder="Max"
                      min="0"
                      value={filters.compensation_max}
                      onChange={handleFilterChange}
                      style={{ width: '50%', padding: '8px' }}
                    />
                  </div>
                </div>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Status</label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    style={{ width: '100%', padding: '8px' }}
                  >
                    <option value="">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Interviewing">Interviewing</option>
                    <option value="Hired">Hired</option>
                    <option value="Rejected">Rejected</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Resume-based Filters */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ marginBottom: '10px' }}>Resume & Profile Filters</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Resume Text Search</label>
                  <input
                    type="text"
                    name="resumeText"
                    placeholder="Search in resume content..."
                    value={filters.resumeText}
                    onChange={handleFilterChange}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Resume URL Contains</label>
                  <input
                    type="text"
                    name="resumeUrl"
                    placeholder="e.g., .pdf, drive.google"
                    value={filters.resumeUrl}
                    onChange={handleFilterChange}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Headline Contains</label>
                  <input
                    type="text"
                    name="headline"
                    placeholder="e.g., Senior Developer"
                    value={filters.headline}
                    onChange={handleFilterChange}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
                <div style={{ minWidth: '200px', flex: '1' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>Tags</label>
                  <input
                    type="text"
                    name="tags"
                    placeholder="e.g., senior, remote"
                    value={filters.tags}
                    onChange={handleFilterChange}
                    style={{ width: '100%', padding: '8px' }}
                  />
                </div>
              </div>
            </div>
            
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button 
                onClick={clearAllFilters}
                style={{ 
                  padding: '8px 16px',
                  marginRight: '10px',
                  backgroundColor: '#f1f1f1',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear All Filters
              </button>
              <button 
                onClick={handleAdvancedSearch}
                style={{ 
                  padding: '8px 16px',
                  backgroundColor: '#4a90e2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}
      
      {/* Column Customization */}
      <div style={{ marginBottom: '20px' }}>
        <details style={{ marginBottom: '10px' }}>
          <summary style={{ cursor: 'pointer', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            Customize Table Columns
          </summary>
          <div style={{ padding: '15px', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {allColumns.map(column => (
                <label key={column.key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[column.key]}
                    onChange={() => handleColumnToggle(column.key)}
                  />
                  {column.label}
                </label>
              ))}
            </div>
          </div>
        </details>
      </div>
      
      {/* Potential Duplicates Section */}
      {potentialDuplicates.length > 0 && (
        <div style={{ 
          padding: '15px', 
          border: '1px solid #ffcc80', 
          borderRadius: '5px',
          marginBottom: '20px',
          backgroundColor: '#fff8e1'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#e65100' }}>
            <span role="img" aria-label="Warning">⚠️</span> Potential Duplicates Detected ({potentialDuplicates.length})
          </h3>
          <p>We found potential duplicate candidates. Consider using the merge function to consolidate records.</p>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
        <button
          onClick={handleMergeSelected}
          disabled={selectedCandidates.length < 2}
          style={{ 
            padding: '8px 12px',
            backgroundColor: selectedCandidates.length >= 2 ? '#4caf50' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedCandidates.length >= 2 ? 'pointer' : 'not-allowed'
          }}
        >
          Merge Selected ({selectedCandidates.length})
        </button>
        
        <button
          onClick={() => {
            const status = prompt('Enter new status (Active, Inactive, Interviewing, Hired, Rejected, On Hold):');
            if (status) handleBulkEdit({ Status: status });
          }}
          disabled={selectedCandidates.length === 0}
          style={{ 
            padding: '8px 12px',
            backgroundColor: selectedCandidates.length > 0 ? '#2196f3' : '#ccc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: selectedCandidates.length > 0 ? 'pointer' : 'not-allowed'
          }}
        >
          Bulk Edit Status
        </button>
        
        <span style={{ fontSize: '14px', color: '#666' }}>
          {selectedCandidates.length} candidate(s) selected
        </span>
      </div>

      {/* Status Messages */}
      {mergeMessage && (
        <div style={{ 
          padding: '10px', 
          marginBottom: '10px', 
          backgroundColor: mergeMessage.includes('error') || mergeMessage.includes('failed') ? '#ffebee' : '#e8f5e8',
          color: mergeMessage.includes('error') || mergeMessage.includes('failed') ? '#c62828' : '#2e7d32',
          borderRadius: '4px'
        }}>
          {mergeMessage}
        </div>
      )}
      
      {bulkEditStatus && (
        <div style={{ 
          padding: '10px', 
          marginBottom: '10px', 
          backgroundColor: bulkEditStatus.includes('error') || bulkEditStatus.includes('failed') ? '#ffebee' : '#e8f5e8',
          color: bulkEditStatus.includes('error') || bulkEditStatus.includes('failed') ? '#c62828' : '#2e7d32',
          borderRadius: '4px'
        }}>
          {bulkEditStatus}
        </div>
      )}

      {/* Results Summary */}
      <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
        Showing {candidates.length} of {pagination.totalRecords} candidates 
        {searchTerm && ` for "${searchTerm}"`}
        {Object.values(filters).some(v => v !== '') && ' (filtered)'}
      </div>

      {/* Main Content Area with Sidebar */}
      <div style={{ display: 'flex', gap: '20px', height: 'calc(100vh - 400px)' }}>
        {/* Main Table */}
        <div style={{ flex: sidebarOpen ? '1' : '1', overflow: 'auto' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <p>Loading candidates...</p>
            </div>
          ) : candidates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <p>No candidates found. Try adjusting your search criteria.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    {allColumns
                      .filter(col => visibleColumns[col.key])
                      .map(column => (
                        <th 
                          key={column.key}
                          style={{ 
                            padding: '12px 8px', 
                            textAlign: 'left', 
                            borderBottom: '2px solid #ddd',
                            fontWeight: 'bold',
                            width: column.width,
                            minWidth: column.width
                          }}
                        >
                          {column.label}
                        </th>
                      ))
                    }
                    <th style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '2px solid #ddd', width: '100px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((candidate, index) => (
                    <tr 
                      key={candidate._id} 
                      style={{ 
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9',
                        borderBottom: '1px solid #eee'
                      }}
                    >
                      {allColumns
                        .filter(col => visibleColumns[col.key])
                        .map(column => (
                          <td 
                            key={column.key}
                            style={{ 
                              padding: '12px 8px', 
                              borderBottom: '1px solid #eee',
                              maxWidth: column.width,
                              overflow: 'hidden'
                            }}
                          >
                            {renderCell(candidate, column.key)}
                          </td>
                        ))
                      }
                      <td style={{ padding: '12px 8px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                        <button
                          onClick={() => handleViewCandidate(candidate)}
                          style={{ 
                            padding: '4px 8px',
                            backgroundColor: '#4a90e2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sidebar */}
        {sidebarOpen && selectedCandidate && (
          <div style={{ 
            width: '400px', 
            backgroundColor: '#f9f9f9', 
            border: '1px solid #ddd', 
            borderRadius: '8px',
            padding: '20px',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Candidate Details</h3>
              <button
                onClick={() => setSidebarOpen(false)}
                style={{ 
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>{selectedCandidate.name || 'N/A'}</h4>
              <p style={{ margin: '0 0 10px 0', color: '#666', fontSize: '14px' }}>
                {selectedCandidate.headline || 'No headline provided'}
              </p>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Contact Information:</strong>
              <div style={{ marginTop: '5px', fontSize: '14px' }}>
                <div>📧 {selectedCandidate.email || 'No email'}</div>
                <div>📞 {selectedCandidate.phone || 'No phone'}</div>
                <div>📍 {selectedCandidate.currentLocation || 'No location'}</div>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Professional Information:</strong>
              <div style={{ marginTop: '5px', fontSize: '14px' }}>
                <div>🏢 {selectedCandidate.currentCompany || 'No company'}</div>
                <div>👔 {selectedCandidate.currentRole || 'No role'}</div>
                <div>⏱️ {selectedCandidate.experience_yrs ? `${selectedCandidate.experience_yrs} years` : 'No experience data'}</div>
                <div>💰 {selectedCandidate.compensation ? (typeof selectedCandidate.compensation === 'number' ? `$${selectedCandidate.compensation.toLocaleString()}` : selectedCandidate.compensation) : 'No compensation data'}</div>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Skills:</strong>
              <div style={{ marginTop: '5px', fontSize: '14px' }}>
                {selectedCandidate.skills ? (
                  Array.isArray(selectedCandidate.skills) 
                    ? selectedCandidate.skills.join(', ') 
                    : selectedCandidate.skills
                ) : 'No skills listed'}
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Status:</strong>
              <div style={{ marginTop: '5px' }}>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  backgroundColor: getStatusColor(selectedCandidate.Status),
                  color: 'white'
                }}>
                  {selectedCandidate.Status || 'Unknown'}
                </span>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <strong>Links:</strong>
              <div style={{ marginTop: '5px', fontSize: '14px' }}>
                {selectedCandidate.linkedin && (
                  <div>
                    <a href={selectedCandidate.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>
                      🔗 LinkedIn Profile
                    </a>
                  </div>
                )}
                {selectedCandidate.resume && selectedCandidate.resume.length > 0 && (
                  <div style={{ marginTop: '5px' }}>
                    <a href={selectedCandidate.resume} target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>
                      📄 Resume
                    </a>
                  </div>
                )}
                {!selectedCandidate.linkedin && (!selectedCandidate.resume || selectedCandidate.resume.length === 0) && (
                  <div style={{ color: '#666' }}>No links available</div>
                )}
              </div>
            </div>

            {selectedCandidate.notes && (
              <div style={{ marginBottom: '15px' }}>
                <strong>Notes:</strong>
                <div style={{ marginTop: '5px', fontSize: '14px', fontStyle: 'italic' }}>
                  {selectedCandidate.notes}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
            <button 
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              Previous
            </button>
            
            <span style={{ margin: '0 10px' }}>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            
            <button 
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateDashboard;
